import type { Components, Theme } from '@mui/material/styles';
import { controlHeight, fonts, radius } from './tokens';

// All colors come from `theme.vars.palette.md` so light/dark switch through CSS variables.
export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      body: {
        WebkitFontSmoothing: 'antialiased',
        textRendering: 'optimizeLegibility',
      },
      '::selection': {
        background: `color-mix(in srgb, ${theme.vars.palette.primary.main} 28%, transparent)`,
      },
      ':focus-visible': {
        outline: `2px solid ${theme.vars.palette.primary.main}`,
        outlineOffset: 2,
      },
      '*::-webkit-scrollbar': { width: 10, height: 10 },
      '*::-webkit-scrollbar-thumb': {
        background: theme.vars.palette.md.borderStrong,
        borderRadius: 99,
        border: '2px solid transparent',
        backgroundClip: 'padding-box',
      },
      '*::-webkit-scrollbar-track': { background: 'transparent' },
      '@keyframes mdFade': {
        from: { opacity: 0, transform: 'translateY(8px)' },
        to: { opacity: 1, transform: 'none' },
      },
      '@keyframes mdSpin': { to: { transform: 'rotate(360deg)' } },
      // Printing (daily report): drop the app chrome and let content flow onto pages.
      '@media print': {
        '.md-no-print': { display: 'none !important' },
        '.md-print-flow': { height: 'auto !important', overflow: 'visible !important' },
        body: { background: '#FFFFFF' },
      },
    }),
  },

  MuiButtonBase: { defaultProps: { disableRipple: true } },

  MuiButton: {
    defaultProps: { disableElevation: true, variant: 'outlined', color: 'inherit' },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.control,
        fontFamily: fonts.body,
        fontWeight: 600,
        fontSize: 14,
        gap: 9,
        whiteSpace: 'nowrap',
        '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 },
        '&.Mui-disabled': { opacity: 0.55 },
        variants: [
          { props: { size: 'medium' }, style: { height: controlHeight.md, padding: '0 15px' } },
          {
            props: { size: 'small' },
            style: { height: controlHeight.sm, padding: '0 12px', fontSize: 12.5 },
          },
          { props: { size: 'large' }, style: { height: 48, padding: '0 22px', fontSize: 15 } },
          {
            // Secondary action ("md-sec"): white panel with a hairline border.
            props: { variant: 'outlined' },
            style: {
              border: `1px solid ${theme.vars.palette.md.border}`,
              background: theme.vars.palette.md.panel,
              color: theme.vars.palette.md.text,
              '& .MuiButton-startIcon': { color: theme.vars.palette.md.muted },
              '&:hover': {
                background: theme.vars.palette.md.panel2,
                borderColor: theme.vars.palette.md.borderStrong,
              },
            },
          },
          {
            // Primary action ("md-primary"): brand gradient, flat like everything else.
            props: { variant: 'contained' },
            style: {
              fontFamily: fonts.head,
              fontWeight: 700,
              color: '#FFFFFF',
              background: theme.vars.palette.md.gradPrimary,
              transition: 'transform .14s, filter .14s',
              '&:hover': {
                filter: 'brightness(1.05)',
                background: theme.vars.palette.md.gradPrimary,
              },
              '&:active': { transform: 'translateY(1px)' },
              '&.Mui-disabled': { color: '#FFFFFF' },
            },
          },
          {
            props: { variant: 'contained', color: 'error' },
            style: { background: theme.vars.palette.md.bad },
          },
          {
            // Inline link-style action ("md-link").
            props: { variant: 'text' },
            style: {
              height: 'auto',
              padding: '2px 4px',
              minWidth: 0,
              fontWeight: 700,
              fontSize: 12.5,
              color: theme.vars.palette.md.primary,
              '&:hover': { background: 'transparent', textDecoration: 'underline' },
            },
          },
        ],
      }),
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.control,
        color: theme.vars.palette.md.muted,
        transition: 'transform .14s',
        '&:hover': {
          background: `color-mix(in srgb, ${theme.vars.palette.md.text} 8%, transparent)`,
          color: theme.vars.palette.md.text,
        },
        '&:active': { transform: 'scale(.94)' },
      }),
    },
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: { root: { backgroundImage: 'none' } },
  },

  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.card,
        border: `1px solid ${theme.vars.palette.md.border}`,
        background: theme.vars.palette.md.panel,
        boxShadow: theme.vars.palette.md.shadow,
      }),
    },
  },

  MuiCardContent: {
    styleOverrides: { root: { padding: 18, '&:last-child': { paddingBottom: 18 } } },
  },

  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: radius.popover,
        border: `1px solid ${theme.vars.palette.md.border}`,
        boxShadow: theme.vars.palette.md.shadowLg,
        backgroundColor: theme.vars.palette.md.panel,
      }),
    },
  },

  MuiMenu: {
    styleOverrides: { list: { padding: 8 } },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.sm,
        fontSize: 13,
        fontWeight: 600,
        gap: 11,
        padding: '9px 12px',
        minHeight: 0,
        '&:hover': { background: theme.vars.palette.md.panel2 },
        '&.Mui-selected, &.Mui-selected:hover': {
          background: theme.vars.palette.md.primary050,
          color: theme.vars.palette.md.primary,
        },
        '& .MuiListItemIcon-root': { minWidth: 0, color: theme.vars.palette.md.faint },
      }),
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: radius.card,
        border: `1px solid ${theme.vars.palette.md.border}`,
        boxShadow: theme.vars.palette.md.shadowLg,
        backgroundColor: theme.vars.palette.md.panel,
        // MUI keeps a 32px gutter round every dialog; on a phone that eats a fifth of the width.
        [theme.breakpoints.down('sm')]: {
          margin: 16,
          maxWidth: 'calc(100% - 32px)',
          maxHeight: 'calc(100% - 32px)',
          '&.MuiDialog-paperFullWidth': { width: 'calc(100% - 32px)' },
        },
      }),
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: { fontFamily: fonts.head, fontWeight: 800, fontSize: 18, padding: '20px 22px 8px' },
    },
  },

  MuiDialogContent: { styleOverrides: { root: { padding: '8px 22px' } } },
  MuiDialogActions: {
    styleOverrides: { root: { padding: '14px 22px 20px', gap: 8, flexWrap: 'wrap' } },
  },

  MuiBackdrop: {
    styleOverrides: {
      root: {
        '&:not(.MuiBackdrop-invisible)': {
          background: 'rgba(6,12,22,.55)',
          backdropFilter: 'blur(2px)',
        },
      },
    },
  },

  MuiTextField: { defaultProps: { fullWidth: true } },

  MuiInputLabel: {
    defaultProps: { shrink: true },
    styleOverrides: {
      root: ({ theme }) => ({
        position: 'static',
        transform: 'none',
        fontSize: 11.5,
        fontWeight: 700,
        color: theme.vars.palette.md.muted,
        marginBottom: 6,
        '&.Mui-focused': { color: theme.vars.palette.md.muted },
        '&.Mui-error': { color: theme.vars.palette.md.badInk },
      }),
    },
  },

  MuiOutlinedInput: {
    defaultProps: { notched: false },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.input,
        background: theme.vars.palette.md.panel,
        fontSize: 14,
        transition: 'box-shadow .14s',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.vars.palette.md.border },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.vars.palette.md.borderStrong,
        },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${theme.vars.palette.md.primary050}` },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.vars.palette.md.primary,
          borderWidth: 1,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: theme.vars.palette.md.bad },
      }),
      // Vertical padding = (height - 1.4375em line box at 14px) / 2, so single-line inputs and
      // selects land exactly on `controlHeight`. Targets the input class, not `input`, so selects
      // (a div) get the same height as text fields.
      input: { padding: '9.9375px 14px' },
      sizeSmall: { '& .MuiInputBase-input': { padding: '6.9375px 12px' } },
    },
  },

  MuiFormHelperText: {
    styleOverrides: { root: { marginLeft: 2, marginTop: 5, fontSize: 11.5, fontWeight: 600 } },
  },

  MuiTooltip: {
    defaultProps: { arrow: false },
    styleOverrides: {
      tooltip: { borderRadius: radius.sm, fontSize: 12, fontWeight: 600, padding: '6px 10px' },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: { borderRadius: radius.control, fontWeight: 700, fontSize: 12 },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottom: `1px solid ${theme.vars.palette.md.border}`,
        fontSize: 14,
        padding: '13px 16px',
      }),
      head: ({ theme }) => ({
        background: theme.vars.palette.md.panel2,
        color: theme.vars.palette.md.muted,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '.07em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        padding: '12px 16px',
      }),
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiTableRow-hover:hover': { background: theme.vars.palette.md.panel2 },
      }),
    },
  },

  MuiTableSortLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover, &.Mui-active': { color: theme.vars.palette.md.text },
      }),
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: 8,
        borderRadius: radius.xs,
        background: theme.vars.palette.md.panel3,
      }),
      bar: { borderRadius: radius.xs },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      switchBase: ({ theme }) => ({
        '&.Mui-checked + .MuiSwitch-track': {
          background: theme.vars.palette.md.primary,
          opacity: 1,
        },
      }),
    },
  },

  MuiCheckbox: { defaultProps: { color: 'primary', size: 'small' } },

  MuiSkeleton: {
    styleOverrides: {
      root: ({ theme }) => ({ backgroundColor: theme.vars.palette.md.panel3 }),
      rounded: { borderRadius: radius.control },
    },
  },

  MuiAlert: {
    styleOverrides: { root: { borderRadius: radius.card, fontSize: 13, fontWeight: 600 } },
  },

  MuiTypography: {
    styleOverrides: {
      root: ({ theme }) => ({
        variants: [
          { props: { variant: 'kicker' }, style: { color: theme.vars.palette.md.primary } },
        ],
      }),
    },
  },
};
